import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, TrendUp, Scales, Airplane, ChartBar, ChartLine, WarningCircle, ListBullets } from "@phosphor-icons/react";
import { DetailedReports } from "../components/DetailedReports";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";

const COLORS = ["#1a2d59", "#33cc33", "#047857", "#F97316", "#3B82F6", "#DB2777", "#EAB308", "#8B5CF6"];
const fmtInr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

export default function Reports() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};

  const [hc, setHc] = useState(null);
  const [attr, setAttr] = useState(null);
  const [comp, setComp] = useState(null);
  const [leaves, setLeaves] = useState(null);

  useEffect(() => {
    api.get("/reports/headcount", { params }).then(({ data }) => setHc(data)).catch(() => {});
    api.get("/reports/attrition", { params }).then(({ data }) => setAttr(data)).catch(() => {});
    api.get("/reports/compensation-equity", { params }).then(({ data }) => setComp(data)).catch(() => {});
    api.get("/reports/leaves-usage", { params }).then(({ data }) => setLeaves(data)).catch(() => {});
    // eslint-disable-next-line
  }, [tenantId]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Analytics</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Reports library</h1>
        <p className="text-sm text-zinc-500 mt-2">Attendance, leave, salary, regularisation, on-duty and geo-tagging reports to view or download — plus headcount, attrition and compensation analytics.</p>
      </div>

      <Tabs defaultValue="detailed">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm flex-wrap h-auto">
          <TabsTrigger value="detailed" className="rounded-sm" data-testid="rep-tab-detailed"><ListBullets size={14} className="mr-1" /> Detailed reports</TabsTrigger>
          <TabsTrigger value="headcount" className="rounded-sm" data-testid="rep-tab-hc"><Users size={14} className="mr-1" /> Headcount</TabsTrigger>
          <TabsTrigger value="attrition" className="rounded-sm" data-testid="rep-tab-attr"><TrendUp size={14} className="mr-1" /> Attrition</TabsTrigger>
          <TabsTrigger value="comp" className="rounded-sm" data-testid="rep-tab-comp"><Scales size={14} className="mr-1" /> Compensation equity</TabsTrigger>
          <TabsTrigger value="leaves" className="rounded-sm" data-testid="rep-tab-leaves"><Airplane size={14} className="mr-1" /> Leaves</TabsTrigger>
        </TabsList>

        <TabsContent value="detailed" className="mt-4"><DetailedReports params={params} /></TabsContent>

        <TabsContent value="headcount" className="mt-4 space-y-4">
          {!hc ? <Loading /> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Active headcount" value={hc.total_active} />
                <Stat label="Joined last 30d" value={hc.new_joins.last_30_days} accent="text-emerald-700" />
                <Stat label="Joined last 90d" value={hc.new_joins.last_90_days} />
                <Stat label="Joined last 1y" value={hc.new_joins.last_365_days} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Panel title="By department" icon={ChartBar}>
                  <div className="h-72">
                    <ResponsiveContainer><BarChart data={hc.by_department} layout="vertical" margin={{ left: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis type="number" fontSize={11} allowDecimals={false} /><YAxis dataKey="name" type="category" fontSize={11} width={110} />
                      <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                      <Bar dataKey="count" fill="#1a2d59" radius={[0, 4, 4, 0]} />
                    </BarChart></ResponsiveContainer>
                  </div>
                </Panel>
                <Panel title="Gender diversity" icon={Users}>
                  <div className="h-72">
                    <ResponsiveContainer><PieChart>
                      <Pie data={Object.entries(hc.by_gender).map(([k, v]) => ({ name: k, value: v }))} dataKey="value" nameKey="name" outerRadius={90} label={(d) => `${d.name}: ${d.value}`}>
                        {Object.keys(hc.by_gender).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                      <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                    </PieChart></ResponsiveContainer>
                  </div>
                </Panel>
              </div>
              <Panel title="Employment type mix" icon={ChartBar}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {Object.entries(hc.by_employment_type).map(([k, v]) => (
                    <div key={k} className="border border-zinc-200 rounded-sm p-3">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{k.replace(/_/g, " ")}</div>
                      <div className="font-display text-2xl font-bold text-adb-navy mt-1">{v}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </TabsContent>

        <TabsContent value="attrition" className="mt-4 space-y-4">
          {!attr ? <Loading /> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Annual attrition" value={`${attr.annualized_attrition_percent}%`} accent={attr.annualized_attrition_percent > 20 ? "text-red-700" : "text-adb-navy"} />
                <Stat label="Exits last 30d" value={attr.exits_30_days} />
                <Stat label="Exits last 90d" value={attr.exits_90_days} />
                <Stat label="Exits last 1y" value={attr.exits_365_days} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Panel title="Monthly exits (last 12 months)" icon={ChartLine}>
                  <div className="h-72">
                    <ResponsiveContainer><LineChart data={attr.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                      <Line dataKey="exits" stroke="#1a2d59" strokeWidth={2} dot={{ r: 3, fill: "#33cc33" }} />
                    </LineChart></ResponsiveContainer>
                  </div>
                </Panel>
                <Panel title="Reasons for leaving" icon={WarningCircle}>
                  <div className="h-72">
                    <ResponsiveContainer><BarChart data={attr.by_reason} layout="vertical" margin={{ left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis type="number" fontSize={11} allowDecimals={false} /><YAxis dataKey="reason" type="category" fontSize={10} width={140} />
                      <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                      <Bar dataKey="count" fill="#33cc33" radius={[0, 4, 4, 0]} />
                    </BarChart></ResponsiveContainer>
                  </div>
                </Panel>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="comp" className="mt-4 space-y-4">
          {!comp ? <Loading /> : (
            <>
              <Panel title="Average CTC by department" icon={Scales}>
                <div className="border border-zinc-200 rounded-sm overflow-x-auto mt-2">
                  <table className="hr-table">
                    <thead><tr><th>Department</th><th>Headcount</th><th>Average annual CTC</th></tr></thead>
                    <tbody>
                      {comp.by_department.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-zinc-500 text-sm">No salary structures yet.</td></tr>}
                      {comp.by_department.map((r) => (
                        <tr key={r.name}><td>{r.name}</td><td className="font-mono-plex text-xs">{r.count}</td><td className="font-mono-plex text-sm font-semibold">{fmtInr(r.avg_ctc)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
              <Panel title="Gender pay comparison" icon={Users}>
                <div className="border border-zinc-200 rounded-sm overflow-x-auto mt-2">
                  <table className="hr-table">
                    <thead><tr><th>Gender</th><th>Headcount</th><th>Average annual CTC</th><th>Gap vs top</th></tr></thead>
                    <tbody>
                      {comp.by_gender.map((r, i) => {
                        const top = comp.by_gender[0]?.avg_ctc || r.avg_ctc || 1;
                        const gap = Math.round(((r.avg_ctc - top) / top) * 100);
                        return (
                          <tr key={r.name}>
                            <td className="capitalize">{r.name}</td>
                            <td className="font-mono-plex text-xs">{r.count}</td>
                            <td className="font-mono-plex text-sm font-semibold">{fmtInr(r.avg_ctc)}</td>
                            <td className="font-mono-plex text-xs">{i === 0 ? "—" : <Badge variant="outline" className={`rounded-sm ${gap < -5 ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>{gap}%</Badge>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </TabsContent>

        <TabsContent value="leaves" className="mt-4">
          {!leaves ? <Loading /> : (
            <Panel title="Approved leaves YTD (days by type)" icon={Airplane}>
              <div className="h-80">
                <ResponsiveContainer><BarChart data={leaves.ytd_days_by_type}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="type" fontSize={11} /><YAxis fontSize={11} />
                  <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                  <Bar dataKey="days" fill="#1a2d59" radius={[3, 3, 0, 0]} />
                </BarChart></ResponsiveContainer>
              </div>
            </Panel>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className={`font-display text-3xl font-bold mt-1 ${accent || "text-adb-navy"}`}>{value}</div>
    </div>
  );
}
function Panel({ title, icon: Icon, children }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5">
      <div className="flex items-center gap-2"><Icon size={16} className="text-adb-navy" /><div className="font-display text-lg font-bold">{title}</div></div>
      {children}
    </div>
  );
}
function Loading() {
  return <div className="border border-zinc-200 bg-white rounded-sm p-12 text-center text-sm text-zinc-500">Loading…</div>;
}
